import { useEffect, useRef } from "react";

/**
 * Closes a modal on Escape — drop into any open/closed modal.
 *
 *   useEscapeClose(open, onClose);
 *
 * Skips when:
 *   - the modal isn't currently open (no global handlers leaked)
 *   - the user is mid-IME composition (e.g. typing Japanese — Escape ends
 *     the candidate window first)
 *   - the user has a `data-no-escape="true"` attribute on the focused input
 *     (escape hatch for forms where Escape should clear instead of close)
 *
 * Implementation note: `onClose` is captured into a ref so the listener
 * effect only re-runs when `open` flips. Otherwise, a parent that re-renders
 * frequently (e.g. the DAW driven by the audio animation loop) would force
 * `removeEventListener` + `addEventListener` 60 times a second — a small but
 * real window where Escape could land between cleanup and re-attach.
 */
export default function useEscapeClose(open, onClose) {
  const onCloseRef = useRef(onClose);
  useEffect(() => { onCloseRef.current = onClose; }, [onClose]);

  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => {
      if (e.key !== "Escape") return;
      if (e.isComposing) return;
      const active = document.activeElement;
      if (active && active.getAttribute && active.getAttribute("data-no-escape") === "true") return;
      e.preventDefault();
      onCloseRef.current?.();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open]);
}
